import { useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetcher, postFetcher, putFetcherKeyed } from "@/lib/swr";
import type { App } from "@/types/domain";
import { EVENT_OPTIONS, type Subscription } from "./webhook-events";

// Add/edit share one dialog; `sub` present = edit.
// apiBase: "/webhooks" (admin, app picker shown) or `/apps/:pid/webhooks`
// (App Settings, fixedPid pins the app and hides the picker).
// v2 起订阅必须绑 app(全局订阅已下线)。
export const WebhookDialog = ({
  sub,
  onSaved,
  apiBase,
  fixedPid,
}: {
  sub?: Subscription;
  onSaved: () => void;
  apiBase: string;
  fixedPid?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [events, setEvents] = useState<string[]>([]);
  const [pid, setPid] = useState<string>("");
  // Admin picker only — App Settings pins the app, no /apps/all fetch (403 for
  // non-admin owners).
  const { data: apps } = useSWR<App[]>(fixedPid ? null : ["/apps/all"], fetcher);

  const { trigger: save, isMutating: saving } = useSWRMutation(
    sub ? `${apiBase}/${sub.id}#edit` : apiBase,
    sub ? putFetcherKeyed : postFetcher,
    {
      onSuccess: () => {
        setOpen(false);
        onSaved();
      },
    }
  );

  const onOpenChange = (o: boolean) => {
    if (o) {
      setUrl(sub?.url ?? "");
      setSecret(sub?.secret ?? "");
      setEvents(sub?.events ?? []);
      setPid(sub?.pid ?? "");
    }
    setOpen(o);
  };

  const valid =
    /^https?:\/\//.test(url) &&
    secret.trim() &&
    events.length > 0 &&
    Boolean(fixedPid || pid);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          {sub ? "Edit" : "Add subscription"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {sub ? "Edit webhook subscription" : "New webhook subscription"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">Callback URL</span>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/wed-hook"
              className="h-8"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">Secret</span>
            <Input
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Subscriber-managed HMAC secret"
              className="h-8"
            />
          </label>
          <div className="space-y-1">
            <span className="text-sm text-muted-foreground">Events</span>
            <div className="grid grid-cols-2 gap-1.5">
              {EVENT_OPTIONS.map((opt) => (
                <label
                  key={opt.key}
                  className={`flex items-center gap-2 text-sm ${
                    opt.v2 ? "text-muted-foreground/60" : ""
                  }`}
                  title={opt.v2 ? "v2 待开放:事件源尚未接入" : opt.key}
                >
                  <Checkbox
                    disabled={opt.v2}
                    checked={events.includes(opt.key)}
                    onCheckedChange={(v) =>
                      setEvents((prev) =>
                        v === true
                          ? [...prev, opt.key]
                          : prev.filter((x) => x !== opt.key)
                      )
                    }
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
          {!fixedPid && (
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">App</span>
              <Select value={pid} onValueChange={setPid}>
                <SelectTrigger size="sm" className="w-full">
                  <SelectValue placeholder="Select an app" />
                </SelectTrigger>
                <SelectContent>
                  {(apps ?? []).filter((a) => !a.archived).map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.id} - {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Subscriptions are bound to one app; only that app&apos;s events
                are delivered.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!valid || saving}
            onClick={() =>
              save({
                url: url.trim(),
                secret: secret.trim(),
                events,
                // fixedPid 时服务端按路由强制 pid,body 里的值不参与
                pid: fixedPid ?? pid,
              })
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
